import React, { useEffect, useState } from 'react';
import { useHeaderController } from './useHeaderController';
import Icon from '../AppIcon';
import Button from '../ui/Button';

const phoneNumber = '+421 905 123 456';

const NAV_LINKS = [
  { href: '#services', label: 'Služby' },
  { href: '#projects', label: 'Referencie' },
  { href: '#about',   label: 'O nás' },
  { href: '#contact', label: 'Kontakt' },
];

const Header = () => {
  const { overHero, hidden } = useHeaderController('#hero', {
    hideOffset: 120,
    rootMarginTop: 72,
  });

  const [mobileOpen, setMobileOpen] = useState(false);

  const goQuote = () => {
    const el = document.querySelector('#quote-form');
    if (!el) return;
    const headerOffset = 80;
    const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    setMobileOpen(false);
  };

  const call = () => (window.location.href = `tel:${phoneNumber}`);

  const surface = overHero
    ? 'bg-transparent text-white shadow-none'
    : 'bg-white/90 backdrop-blur-md text-primary shadow-md';

  const visibility = hidden ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100';

  // Блокируем скролл страницы под открытым меню
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileOpen]);

  // Закрываем мобильное меню при переходе на md+
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => { if (mq.matches) setMobileOpen(false); };
    try { mq.addEventListener('change', onChange); } catch { mq.addListener(onChange); }
    return () => {
      try { mq.removeEventListener('change', onChange); } catch { mq.removeListener(onChange); }
    };
  }, []);

  // Закрытие по Esc
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header
      data-over-hero={overHero}
      data-hidden={hidden}
      className={[
        'fixed top-0 left-0 right-0 z-50',
        'transition-[transform,background-color,opacity,box-shadow] duration-300 will-change-transform',
        surface,
        visibility,
      ].join(' ')}
    >
      <div className="container mx-auto px-4">
        <div className="h-16 md:h-20 flex items-center justify-between gap-3 md:gap-4">
          {/* LOGO */}
          <a
            href="/"
            className={[
              'shrink-0 inline-flex items-center gap-2 md:gap-2.5',
              'h-10 px-2 rounded-md',
              overHero ? 'text-white hover:bg-white/10' : 'text-primary hover:bg-primary/5',
            ].join(' ')}
            aria-label="Domov, Balcony-Glazing"
          >
            <Icon name="Home" size={22} />
            <span
              className={[
                'font-semibold leading-none tracking-wide',
                overHero ? 'text-white' : 'text-primary',
              ].join(' ')}
            >
              Balcony-Glazing
            </span>
          </a>

          {/* NAV (desktop/tablet) */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8 text-sm font-medium">
            {NAV_LINKS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={[
                  'hover:opacity-80 transition-opacity font-semibold',
                  overHero ? 'text-white' : 'text-primary',
                ].join(' ')}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* ACTIONS */}
          <div className="flex items-center gap-1.5 md:gap-3">
            {/* На планшете показываем только иконку телефона, номер — с lg */}
            <Button
              variant="ghost"
              size="sm"
              onClick={call}
              title={phoneNumber}
              aria-label={`Zavolať ${phoneNumber}`}
              className={[
                overHero ? '!text-white hover:!bg-white/10' : '!text-primary hover:!bg-primary/5',
              ].join(' ')}
              iconName="Phone"
              iconPosition="left"
            >
              <span className="hidden lg:inline">{phoneNumber}</span>
            </Button>

            <Button
              variant="default"
              size="sm"
              onClick={goQuote}
              iconName="Calculator"
              iconPosition="left"
              className="bg-accent hover:bg-accent/90 text-white hidden md:inline-flex"
            >
              Cenový odhad
            </Button>

            {/* Бургер только для мобилок */}
            <button
              className={[
                'md:hidden inline-flex items-center justify-center size-10 rounded-md',
                overHero ? 'text-white hover:bg-white/10' : 'text-primary hover:bg-primary/5',
              ].join(' ')}
              aria-label="Menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-dropdown"
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
            >
              <Icon name={mobileOpen ? 'X' : 'Menu'} size={22} />
            </button>
          </div>
        </div>
      </div>

      {/* MOBILE OVERLAY (только мобилки) */}
      <div
        className={[
          'fixed inset-0 z-[60] bg-black/50 transition-opacity duration-300 md:hidden',
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
        onClick={() => setMobileOpen(false)}
      />

      {/* MOBILE DROPDOWN (выпадает сверху вниз из-под хедера) */}
      <section
        id="mobile-dropdown"
        role="dialog"
        aria-modal="true"
        className={[
          'fixed inset-x-0 top-16 z-[61] md:hidden',
          overHero ? 'bg-neutral-900 text-white' : 'bg-white text-primary',
          'shadow-xl border-b',
          overHero ? 'border-white/10' : 'border-border',
          'origin-top transition-transform duration-300 will-change-transform',
          mobileOpen ? 'scale-y-100' : 'scale-y-0 pointer-events-none',
          'max-h-[calc(100vh-4rem)] overflow-y-auto',
        ].join(' ')}
      >
        <nav className="px-2 py-3">
          {NAV_LINKS.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className={[
                'block px-4 py-3 text-base font-semibold rounded-md',
                overHero ? 'text-white hover:bg-white/10' : 'text-primary hover:bg-primary/5',
              ].join(' ')}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="px-4 pt-2 pb-6 space-y-3">
          <Button
            variant="default"
            size="md"
            onClick={goQuote}
            iconName="Calculator"
            iconPosition="left"
            className="w-full bg-accent hover:bg-accent/90 text-white"
          >
            Cenový odhad
          </Button>
          <Button
            variant="ghost"
            size="md"
            onClick={call}
            iconName="Phone"
            iconPosition="left"
            className={[
              'w-full',
              overHero ? 'text-white hover:bg-white/10' : 'text-primary hover:bg-primary/5',
            ].join(' ')}
          >
            {phoneNumber}
          </Button>
        </div>
      </section>
    </header>
  );
};

export default Header;